/**
 * Admin routes for store management
 * All routes under /admin/store/:slug/...
 *
 * Authentication: Uses federated staff auth from Lytning POS (not regular user JWT)
 */

import type { RouteRecordRaw } from 'vue-router'
import { staffAuthGuard } from './guards/staffAuth'

export const adminRoutes: RouteRecordRaw[] = [
  // Auth error page (no auth required)
  {
    path: '/admin/auth/error',
    name: 'AdminAuthError',
    component: () => import('@/views/staff/StaffAuthErrorView.vue'),
    meta: { 
      title: 'Authentication Error'
    }
  },
  // Admin store management (requires staff auth)
  {
    path: '/admin/store/:slug',
    component: () => import('@/layouts/AdminLayout.vue'),
    beforeEnter: staffAuthGuard,
    meta: { 
      requiresStaffAuth: true
    },
    children: [
      {
        path: '',
        name: 'AdminDashboard',
        redirect: (to) => ({
          name: 'AdminDeliveries',
          params: { slug: to.params.slug }
        })
      },
      {
        path: 'deliveries',
        name: 'AdminDeliveries',
        component: () => import('@/views/admin/deliveries/DeliveryDashboardPage.vue'),
        meta: { 
          title: 'Deliveries',
          breadcrumb: 'Deliveries',
          icon: 'mdi-truck-fast',
          staffPermissions: ['deliveries:read']
        }
      },
      {
        path: 'deliveries/:orderId',
        name: 'AdminDeliveryDetail',
        component: () => import('@/views/admin/deliveries/DeliveryDetailPage.vue'),
        props: true,
        meta: { 
          title: 'Delivery Details',
          breadcrumb: 'Details',
          icon: 'mdi-truck-fast',
          staffPermissions: ['deliveries:read']
        }
      },
      {
        path: 'prescriptions',
        name: 'AdminRxQueue',
        component: () => import('@/views/admin/prescriptions/RxQueuePage.vue'),
        meta: {
          title: 'Prescriptions',
          breadcrumb: 'Prescriptions',
          icon: 'mdi-prescription',
          staffPermissions: ['prescriptions:read'],
        },
      },
      {
        path: 'prescriptions/:rxId',
        name: 'AdminRxDetail',
        component: () => import('@/views/admin/prescriptions/AdminRxDetailPage.vue'),
        props: true,
        meta: {
          title: 'Prescription Details',
          breadcrumb: 'Details',
          icon: 'mdi-prescription',
          staffPermissions: ['prescriptions:read'],
        },
      },
      {
        path: 'products',
        name: 'AdminProducts',
        component: () => import('@/views/admin/AdminComingSoonPage.vue'),
        meta: { 
          title: 'Products',
          breadcrumb: 'Products',
          icon: 'mdi-package-variant-closed',
          comingSoon: true,
          staffPermissions: ['products:read']
        }
      },
      {
        path: 'orders',
        name: 'AdminOrders',
        component: () => import('@/views/admin/AdminComingSoonPage.vue'),
        meta: { 
          title: 'Orders',
          breadcrumb: 'Orders',
          icon: 'mdi-clipboard-list',
          comingSoon: true,
          staffPermissions: ['orders:read']
        }
      },
      {
        path: 'settings',
        name: 'AdminSettings',
        component: () => import('@/views/admin/settings/AdminSettingsPage.vue'),
        meta: { 
          title: 'Settings',
          breadcrumb: 'Settings',
          icon: 'mdi-cog',
          staffPermissions: ['settings:read']
        }
      },
    ],
  },
]
